/**
 * Query + mutation hooks for `/api/teams/{tid}/imports/*` (#1873).
 *
 * All lead-only. Uploads keep the workbook bytes on the server (same bytes
 * → same source row); previews are read-only and reuse a batch when the
 * inputs have not changed. `useApplyImportBatch` is the one write to the
 * roster: a reviewed leave-projection preview becomes native leave rows, a
 * call roster becomes on-call pins and a stations preview (the ward list)
 * becomes team stations (#1873 slice 3); `dry_run` returns the same plan
 * without writing.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { apiFetch } from "./client";
import type { Changes, PreviewEnvelope } from "./importEnvelope";
import type { components } from "./schema.gen";

/** A layout fact a lead may set under `options.layout` (#1873 I4); the API
 * types the list as empty objects, so it is spelled out here. */
export type ImportAnchor = {
  name: string;
  label: string;
  kind: "row" | "column" | "cell" | "column_map";
  required?: boolean;
  help?: string;
  /** `column_map` only: the meanings a column may take; absent when the
   * meaning is free text (a grade the department names) */
  choices?: { value: string; label: string }[];
  /** `column_map` without choices: the shape of a meaning, e.g. `REGISTRAR:AM` */
  placeholder?: string;
};
/** One anchor's value as the mapper holds it: a row / column / cell as text,
 * or a `column_map` as `{ letter: meaning }` (a picked column may still be
 * blank until the lead chooses its meaning; blanks are never posted). */
export type LayoutValue = string | Record<string, string>;
export type LayoutValues = Record<string, LayoutValue>;

/** The anchors to post as `options.layout`: blank values dropped, a column
 * map kept only with its non-blank columns. */
export function layoutForPreview(values: LayoutValues): LayoutValues {
  const out: LayoutValues = {};
  for (const [name, value] of Object.entries(values)) {
    if (typeof value === "string") {
      if (value.trim() !== "") out[name] = value.trim();
      continue;
    }
    const columns = Object.entries(value).filter(([, meaning]) => meaning.trim() !== "");
    if (columns.length) out[name] = Object.fromEntries(columns);
  }
  return out;
}

/** `options.layout` as a batch recorded it, back into mapper values. */
export function layoutFromBatch(used: unknown): LayoutValues {
  if (!used || typeof used !== "object") return {};
  const out: LayoutValues = {};
  for (const [name, value] of Object.entries(used as Record<string, unknown>)) {
    if (typeof value === "string" || typeof value === "number") out[name] = String(value);
    else if (value && typeof value === "object")
      out[name] = Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, String(v ?? "")]),
      );
  }
  return out;
}

/** A `column_map` anchor as one line for a badge: `D→REGISTRAR:AM · E→JUNIOR:AM`. */
export function describeLayoutValue(value: unknown, anchor?: ImportAnchor): string {
  if (!value || typeof value !== "object") return value == null ? "" : String(value);
  const labels = new Map((anchor?.choices ?? []).map((c) => [c.value, c.label] as const));
  return Object.entries(value as Record<string, unknown>)
    .map(([letter, meaning]) => `${letter}→${labels.get(String(meaning)) ?? String(meaning)}`)
    .join(" · ");
}
export type ImportProfile = Omit<components["schemas"]["ImportProfileRead"], "anchors"> & {
  anchors?: ImportAnchor[];
};
export type SheetCells = components["schemas"]["SheetCellsRead"];
export type ImportSource = components["schemas"]["ImportSourceRead"];
export type SheetInfo = components["schemas"]["SheetInfo"];
export type ImportBatchSummary = components["schemas"]["ImportBatchSummary"];
export type ImportAlias = components["schemas"]["AliasRead"];
export type AliasCreate = components["schemas"]["AliasCreate"];
export type AliasBulkCreate = components["schemas"]["AliasBulkCreate"];
export type ImportApplyRequest = components["schemas"]["ApplyRequest"];
export type ImportApplyOutcome = components["schemas"]["ImportApplyOutcome"];
/** `PreviewRequest` with `options` widened: the API types the free-form
 * profile options as an empty object, which openapi-typescript renders as
 * `Record<string, never>`. */
export type PreviewRequest = Omit<components["schemas"]["PreviewRequest"], "options"> & {
  options?: Record<string, unknown>;
};

/** `ImportBatchRead` with the envelope narrowed to the shape the review reads. */
export type ImportBatch = Omit<components["schemas"]["ImportBatchRead"], "envelope"> & {
  envelope: PreviewEnvelope;
};

export const IMPORTS_KEY = (teamId: string, ...rest: string[]) => ["imports", teamId, ...rest];

function useTeamQuery<T>(teamId: string, key: string[], path: string, enabled = true) {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: [...IMPORTS_KEY(teamId, ...key), accountId ?? ""],
    enabled: Boolean(teamId && accountId && enabled),
    retry: false,
    queryFn: () => apiFetch<T>(path),
  });
}

export function useImportProfiles(teamId: string) {
  return useTeamQuery<ImportProfile[]>(
    teamId,
    ["profiles"],
    `/api/teams/${teamId}/imports/profiles`,
  );
}

export function useImportSources(teamId: string) {
  return useTeamQuery<ImportSource[]>(teamId, ["sources"], `/api/teams/${teamId}/imports/sources`);
}

/** One resolved window of a kept workbook's sheet, for the layout mapper (#1873 I4). */
export function useImportSheetCells(
  teamId: string,
  sourceId: string | null,
  sheet: string | null,
  window: { firstRow: number; rows: number; firstCol: string; cols: number },
) {
  const params = new URLSearchParams({
    sheet: sheet ?? "",
    first_row: String(window.firstRow),
    rows: String(window.rows),
    first_col: window.firstCol,
    cols: String(window.cols),
  });
  return useTeamQuery<SheetCells>(
    teamId,
    ["cells", sourceId ?? "", params.toString()],
    `/api/teams/${teamId}/imports/sources/${sourceId}/cells?${params.toString()}`,
    Boolean(sourceId && sheet),
  );
}

export function useImportBatches(teamId: string) {
  return useTeamQuery<ImportBatchSummary[]>(
    teamId,
    ["batches"],
    `/api/teams/${teamId}/imports/batches`,
  );
}

export type UnboundCall = components["schemas"]["UnboundCallRead"];

/** #1883 / Build 19 — imported calls with no roster window yet. */
export function useUnboundCalls(teamId: string) {
  return useTeamQuery<UnboundCall[]>(
    teamId,
    ["unbound-calls"],
    `/api/teams/${teamId}/imports/unbound-calls`,
  );
}

export function useImportBatch(teamId: string, batchId: string | null) {
  return useTeamQuery<ImportBatch>(
    teamId,
    ["batch", batchId ?? ""],
    `/api/teams/${teamId}/imports/batches/${batchId}`,
    Boolean(batchId),
  );
}

export function useImportChanges(teamId: string, batchId: string | null, against: string | null) {
  return useTeamQuery<Changes>(
    teamId,
    ["changes", batchId ?? "", against ?? ""],
    `/api/teams/${teamId}/imports/batches/${batchId}/changes?against=${against}`,
    Boolean(batchId && against),
  );
}

export function useImportAliases(teamId: string) {
  return useTeamQuery<ImportAlias[]>(teamId, ["aliases"], `/api/teams/${teamId}/imports/aliases`);
}

export function useUploadImportSource(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file, file.name);
      // `apiFetch` leaves Content-Type unset for FormData so the browser
      // writes the multipart boundary itself (#1735).
      return apiFetch<ImportSource>(`/api/teams/${teamId}/imports/sources`, {
        method: "POST",
        body: form,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "sources") });
    },
  });
}

export function useDiscardImportSource(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch<null>(`/api/teams/${teamId}/imports/sources/${sourceId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "sources") });
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "batches") });
    },
  });
}

export function useRunImportPreview(teamId: string) {
  const queryClient = useQueryClient();
  const { accountId } = useAuth();
  return useMutation({
    mutationFn: (body: PreviewRequest) =>
      apiFetch<ImportBatch>(`/api/teams/${teamId}/imports/previews`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (batch) => {
      // The POST answers with the whole batch (`ImportBatchRead`, the shape
      // `GET …/batches/{id}` reads), so seed that read under its real key and
      // the review draws from it at once. Left empty, the review unmounted to
      // a skeleton while a second GET ran: `Preview again` collapsed the page
      // under the lead, and a fresh preview drew its review a round trip late.
      queryClient.setQueryData([...IMPORTS_KEY(teamId, "batch", batch.id), accountId ?? ""], batch);
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "batches") });
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "batch", batch.id) });
    },
  });
}

export function useApplyImportBatch(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, body }: { batchId: string; body: ImportApplyRequest }) =>
      apiFetch<ImportApplyOutcome>(`/api/teams/${teamId}/imports/batches/${batchId}/apply`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (outcome, { batchId }) => {
      if (outcome.dry_run) return;
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "batches") });
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "batch", batchId) });
      // the rows now exist on the team's date-request surfaces; a stations
      // apply changed the team config (Setup → Stations reads it)
      queryClient.invalidateQueries({ queryKey: ["team-config", teamId] });
      queryClient.invalidateQueries({ queryKey: ["date-requests", teamId] });
      queryClient.invalidateQueries({ queryKey: ["team-date-requests", teamId] });
      // #2277 — imported leave moves the daily leave-quota counts.
      queryClient.invalidateQueries({ queryKey: ["leave-quota", teamId] });
      // backlog #112 — a calls apply binds (or re-binds) imported calls and
      // writes the on-call pins. Both surfaces are on the page the lead is
      // already looking at, so they must not wait for a navigation: the
      // "waiting for a roster window" card kept the station the lead had just
      // corrected, and the roster grid kept the old pins.
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "unbound-calls") });
      queryClient.invalidateQueries({ queryKey: ["cycle-fixed-shifts"] });
    },
  });
}

export function useRecordImportAlias(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AliasCreate) =>
      apiFetch<ImportAlias>(`/api/teams/${teamId}/imports/aliases`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "aliases") });
    },
  });
}

/** Every "sure" alias in one call — all or nothing (KKH S4 bulk accept). */
export function useRecordImportAliasesBulk(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AliasBulkCreate) =>
      apiFetch<ImportAlias[]>(`/api/teams/${teamId}/imports/aliases/bulk`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "aliases") });
    },
  });
}

export function useRemoveImportAlias(teamId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (aliasId: string) =>
      apiFetch<null>(`/api/teams/${teamId}/imports/aliases/${aliasId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: IMPORTS_KEY(teamId, "aliases") });
    },
  });
}
